<template>
    <div>
        <BackHeader
            title="Modos de práctica"
            :onBack="() => $router.push('/configuracion')"
        ></BackHeader>

        <p class="mb-6">
            Son las modalidades que el alumno elige al inscribir su práctica, y
            cada una indica a qué tipo de práctica aplica. Un modo no se
            elimina: las prácticas que ya lo usan lo conservan. Al inhabilitarlo
            deja de ofrecerse en las inscripciones nuevas, y se puede volver a
            activar cuando haga falta.
        </p>

        <v-row>
            <!-- Lista de modos -->
            <v-col cols="12" md="4">
                <v-card class="pa-3">
                    <v-btn
                        block
                        elevation="0"
                        color="primary"
                        class="mb-3"
                        :disabled="creando"
                        @click="siNoHayCambios(crear)"
                    >
                        <v-icon left small>mdi-plus</v-icon>
                        Nuevo modo
                    </v-btn>

                    <div
                        v-for="modo in ordenados"
                        :key="modo.id"
                        class="fila"
                        :class="{ activa: esActivo(modo), inhabilitada: !modo.estado }"
                        @click="seleccionar(modo)"
                    >
                        <v-icon small class="mr-2">
                            {{ modo.estado ? "mdi-calendar-clock" : "mdi-cancel" }}
                        </v-icon>
                        <div class="texto">
                            <div class="font-weight-medium">{{ modo.nombre }}</div>
                            <div class="caption">
                                {{ detalle(modo) }}<template v-if="!modo.estado"> · Inhabilitado</template>
                            </div>
                        </div>
                    </div>

                    <p
                        v-if="!modos.length && !cargando"
                        class="caption text-center my-3"
                    >
                        Todavía no hay modos de práctica.
                    </p>
                </v-card>
            </v-col>

            <!-- Edición del elegido -->
            <v-col cols="12" md="8" class="panel-edicion">
                <v-card v-if="formulario" class="pa-4">
                    <v-alert
                        v-if="formulario.id && !original.estado"
                        type="info"
                        text
                        dense
                    >
                        Este modo está inhabilitado: no se ofrece al inscribir una
                        práctica nueva.
                    </v-alert>

                    <v-text-field
                        v-model="formulario.nombre"
                        label="Nombre"
                        filled
                        :error-messages="errores.nombre"
                    ></v-text-field>

                    <v-text-field
                        v-model.number="formulario.cantidad_horas"
                        label="Cantidad de horas"
                        type="number"
                        min="1"
                        filled
                        :error-messages="errores.cantidad_horas"
                    >
                        <template v-slot:append>
                            <v-tooltip top max-width="280">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon v-bind="attrs" v-on="on">
                                        mdi-help-circle-outline
                                    </v-icon>
                                </template>
                                Cantidad de horas de trabajo que el alumno debe
                                cumplir en la práctica.
                            </v-tooltip>
                        </template>
                    </v-text-field>

                    <p class="mb-1">Aplica a</p>
                    <v-switch
                        v-model="formulario.aplica_industrial"
                        @change="errorGeneral = ''"
                        label="Práctica industrial"
                        inset
                        hide-details
                        class="mt-1"
                    ></v-switch>
                    <v-switch
                        v-model="formulario.aplica_profesional"
                        @change="errorGeneral = ''"
                        label="Práctica profesional"
                        inset
                        hide-details
                        class="mt-3 mb-5"
                    ></v-switch>

                    <v-alert v-if="errorGeneral" type="error" text dense>
                        {{ errorGeneral }}
                    </v-alert>

                    <v-card-actions class="px-0">
                        <v-btn
                            elevation="0"
                            color="primary"
                            :disabled="!hayCambios || guardando"
                            @click="guardar"
                        >
                            {{ formulario.id ? "Guardar cambios" : "Crear modo" }}
                        </v-btn>
                        <v-spacer></v-spacer>
                        <template v-if="formulario.id">
                            <v-btn
                                v-if="original.estado"
                                text
                                color="error"
                                :disabled="guardando"
                                @click="siNoHayCambios(pedirInhabilitar)"
                            >
                                Eliminar modo
                            </v-btn>
                            <v-btn
                                v-else
                                text
                                color="primary"
                                :disabled="guardando"
                                @click="siNoHayCambios(activar)"
                            >
                                Volver a activar
                            </v-btn>
                        </template>
                        <v-btn v-else text @click="cerrar">Cancelar</v-btn>
                    </v-card-actions>
                </v-card>

                <v-card v-else class="pa-8 text-center">
                    <p class="mb-0">Elige un modo de la lista para editarlo.</p>
                </v-card>
            </v-col>
        </v-row>

        <ActionModal
            :active="confirmarInhabilitar"
            title="Eliminar modo"
            :successAction="inhabilitar"
            :cancelAction="() => (confirmarInhabilitar = false)"
        >
            {{ textoInhabilitar }}
        </ActionModal>

        <ActionModal
            :active="confirmarDescarte"
            title="Cambios sin guardar"
            :successAction="descartarYSeguir"
            :cancelAction="() => ((confirmarDescarte = false), (pendiente = null))"
        >
            Tienes cambios sin guardar. Si continúas se pierden.
        </ActionModal>
    </div>
</template>

<script>
import BackHeader from "@/components/content/BackHeader";
import ActionModal from "@/components/ActionModal";
import { tablesService } from "@/services";

const ENDPOINT = "practica/modos";
const CAMPOS = ["nombre", "cantidad_horas", "aplica_industrial", "aplica_profesional"];

function sinErrores() {
    return { nombre: [], cantidad_horas: [], aplica_industrial: [], aplica_profesional: [] };
}

export default {
    components: { BackHeader, ActionModal },

    data: () => ({
        // { id, nombre, cantidad_horas, estado, aplica_industrial,
        // aplica_profesional, nivel_de_duracion }, activos e inhabilitados. El
        // nivel_de_duracion es interno y aquí no se muestra.
        modos: [],
        // { id, nombre, cantidad_horas, aplica_industrial, aplica_profesional };
        // sin id mientras es un modo nuevo que todavía no se guarda.
        formulario: null,
        original: null,
        cargando: true,
        guardando: false,
        confirmarInhabilitar: false,
        confirmarDescarte: false,
        // Lo que se hace si el administrador acepta descartar sus cambios.
        pendiente: null,
        // Se fija al abrir el aviso para que no cambie mientras se desvanece.
        textoInhabilitar: "",
        errores: sinErrores(),
        // Un error que no es de un campo, como un modo que no aplica a ningún
        // tipo de práctica.
        errorGeneral: "",
    }),

    computed: {
        // Primero los activos y después los inhabilitados, cada grupo por id.
        ordenados() {
            return this.modos
                .slice()
                .sort((a, b) => b.estado - a.estado || a.id - b.id);
        },

        creando() {
            return !!this.formulario && !this.formulario.id;
        },

        hayCambios() {
            if (!this.formulario || !this.original) {
                return false;
            }
            return CAMPOS.some(
                (campo) => this.formulario[campo] !== this.original[campo]
            );
        },
    },

    created() {
        this.cargar();
    },

    methods: {
        cargar() {
            this.cargando = true;
            // Sin el parámetro, el backend devuelve solo los activos.
            tablesService
                .getService(ENDPOINT, "?incluir_inactivos=1")
                .then((modos) => {
                    this.modos = modos;
                    this.cargando = false;
                })
                .catch(() => {
                    this.cargando = false;
                    this.$store.dispatch(
                        "alert/error",
                        "No se pudieron cargar los modos de práctica."
                    );
                });
        },

        esActivo(modo) {
            return !!this.formulario && this.formulario.id === modo.id;
        },

        seleccionar(modo) {
            if (this.esActivo(modo)) {
                return;
            }
            this.siNoHayCambios(() => this.abrir(modo));
        },

        // Todo lo que reemplaza el formulario o cambia el modo pasa por aquí:
        // con cambios sin guardar, primero se pide confirmación.
        siNoHayCambios(accion) {
            if (this.hayCambios) {
                this.pendiente = accion;
                this.confirmarDescarte = true;
                return;
            }
            accion();
        },

        descartarYSeguir() {
            this.confirmarDescarte = false;
            const accion = this.pendiente;
            this.pendiente = null;
            this.errorGeneral = "";
            if (this.original) {
                this.formulario = { ...this.formulario, ...this.camposDe(this.original) };
            }
            if (accion) {
                accion();
            }
        },

        camposDe(modo) {
            return {
                nombre: modo.nombre,
                cantidad_horas: modo.cantidad_horas,
                aplica_industrial: modo.aplica_industrial,
                aplica_profesional: modo.aplica_profesional,
            };
        },

        // "180 horas · PI y PP"
        detalle(modo) {
            const partes = [`${modo.cantidad_horas} horas`];
            const tipos = [];
            if (modo.aplica_industrial) {
                tipos.push("PI");
            }
            if (modo.aplica_profesional) {
                tipos.push("PP");
            }
            partes.push(tipos.join(" y "));
            return partes.join(" · ");
        },

        abrir(modo) {
            this.errores = sinErrores();
            this.errorGeneral = "";
            this.formulario = { id: modo.id, ...this.camposDe(modo) };
            // Copia aparte para saber si hay cambios sin guardar; lleva el
            // estado para decidir qué botón mostrar.
            this.original = { ...this.formulario, estado: modo.estado };
        },

        // Uno nuevo no se crea en el backend hasta guardarlo: nombre y horas
        // son obligatorios y un alta vacía la rechazaría.
        crear() {
            this.abrir({
                id: null,
                nombre: "",
                cantidad_horas: null,
                aplica_industrial: true,
                aplica_profesional: true,
                estado: true,
            });
        },

        cerrar() {
            this.formulario = null;
            this.original = null;
        },

        // Reemplaza el modo en la lista y lo deja abierto tal como quedó.
        actualizar(modo) {
            const posicion = this.modos.findIndex((item) => item.id === modo.id);
            if (posicion === -1) {
                this.modos.push(modo);
            } else {
                this.$set(this.modos, posicion, modo);
            }
            this.abrir(modo);
        },

        guardar() {
            this.guardando = true;
            this.errores = sinErrores();
            this.errorGeneral = "";

            const datos = {
                nombre: this.formulario.nombre,
                aplica_industrial: this.formulario.aplica_industrial,
                aplica_profesional: this.formulario.aplica_profesional,
                // Un campo numérico vacío llega como "": el backend lo
                // rechaza igual que un nulo y dice que es obligatorio.
                cantidad_horas:
                    this.formulario.cantidad_horas === ""
                        ? null
                        : this.formulario.cantidad_horas,
            };
            const nuevo = !this.formulario.id;
            const peticion = nuevo
                ? tablesService.newService(ENDPOINT, datos)
                : tablesService.editService(ENDPOINT, `${this.formulario.id}/`, datos);

            peticion
                .then((modo) => {
                    this.actualizar(modo);
                    this.guardando = false;
                    this.$store.dispatch(
                        "alert/success",
                        nuevo ? "Modo creado." : "Modo guardado."
                    );
                })
                .catch((error) => {
                    this.guardando = false;
                    this.mostrarErrores(error);
                });
        },

        pedirInhabilitar() {
            this.textoInhabilitar =
                `¿Eliminar «${this.original.nombre}»? Dejará de ofrecerse al ` +
                "inscribir una práctica, pero las prácticas que ya lo usan lo " +
                "conservan. Puedes volver a activarlo cuando quieras.";
            this.confirmarInhabilitar = true;
        },

        // El DELETE del backend no borra la fila: la deja inhabilitada.
        inhabilitar() {
            this.confirmarInhabilitar = false;
            this.cambiarEstado(
                tablesService.deleteService(ENDPOINT, `${this.formulario.id}/`),
                "Modo inhabilitado.",
                "No se pudo eliminar el modo."
            );
        },

        activar() {
            this.cambiarEstado(
                tablesService.editService(ENDPOINT, `${this.formulario.id}/`, {
                    estado: true,
                }),
                "Modo activado.",
                "No se pudo activar el modo."
            );
        },

        cambiarEstado(peticion, exito, fallo) {
            this.guardando = true;
            peticion
                .then((modo) => {
                    this.actualizar(modo);
                    this.guardando = false;
                    this.$store.dispatch("alert/success", exito);
                })
                .catch(() => {
                    this.guardando = false;
                    this.$store.dispatch("alert/error", fallo);
                });
        },

        mostrarErrores(error) {
            const datos = error && error.response && error.response.data;
            const errores = sinErrores();
            if (datos) {
                CAMPOS.forEach((campo) => {
                    const valor = datos[campo];
                    errores[campo] = !valor ? [] : Array.isArray(valor) ? valor : [valor];
                });
            }
            this.errores = errores;
            const generales = datos && datos.non_field_errors;
            if (generales && generales.length) {
                this.errorGeneral = generales.join(" ");
            } else if (!CAMPOS.some((campo) => errores[campo].length)) {
                this.$store.dispatch("alert/error", "No se pudo guardar.");
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.fila {
    display: flex;
    align-items: center;
    padding: 8px;
    border-radius: 6px;
    cursor: pointer;
    border: 1px solid transparent;

    &:hover {
        background-color: rgba(128, 128, 128, 0.12);
    }

    &.activa {
        border-color: var(--v-primary-base);
    }

    // Se puede elegir igual, para editarlo o volver a activarlo.
    &.inhabilitada {
        opacity: 0.5;
    }

    .texto {
        flex: 1;
        min-width: 0;

        div {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }
}

.fila + .fila {
    margin-top: 4px;
}

@media (min-width: 960px) {
    .panel-edicion {
        position: sticky;
        top: 12px;
        align-self: flex-start;
    }
}
</style>
